/**
 * Dialog
 * <uo-dialog>
 *
 * Attributes:
 * heading
 * size
 * open
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, query, property } from 'lit/decorators.js';

// Import animations
import { fadeOut } from '@automator/animations';

// Import style for web component
const styles = require( './web-component-style.scss' );

// eslint-disable-next-line require-jsdoc
@customElement( 'uo-dialog' )
export class AutomatorDialog extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Get wrapper
	@query( '.wrapper' ) $wrapper;

	// Properties
	@property({ type: String }) heading;
	@property({ type: String }) size;
	@property({ type: Boolean }) open;

	// Defines if the dialog can only be forced by invoking .close()
	// If set to true, the dialog will be closed when pressing Esc or when clicking outside the box
	@property({ type: Boolean, attribute: 'force-manual-close' }) forceManualClose = false;

	// eslint-disable-next-line require-jsdoc
	constructor() {
		super();

		// [internal] Define the action attribute
		this.actionAttribute = 'dialog-action';
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div 
				class="wrapper"
				?open=${ this.open }

				@click=${ this._handleClickOutside }
			>
				<div 
					class="dialog"
					size="${ this.size }"
				>
					<div class="dialog-heading">${ unsafeHTML( this.heading ) }</div>

					<div class="dialog-content">
						<slot></slot>
					</div>

					<div class="dialog-footer" @click=${ this._handleClick }>
						<slot name="secondary-action"></slot>
						<slot name="primary-action"></slot>
					</div>
				</div>
			</div>
		`;
	}

	/**
	 * Disable scroll on body when the dialog is added
	 * 
	 * @return {undefined}
	 */
	connectedCallback() {
		super.connectedCallback();
		
		// Disable scroll on body element
		document.body.classList.add( 'uap-disable-scroll' );

		// Remove focus from current focused element (if there is one)
		// We need to do this if we want to use autofocus on fields inside the dialog
		if ( document.activeElement ) {
			document.activeElement.blur();
		}
	}

	/**
	 * Enable scroll on body when the dialog is added
	 * 
	 * @return {undefined}
	 */
	disconnectedCallback() {
		super.connectedCallback();
		
		// Disable scroll on body element
		document.body.classList.remove( 'uap-disable-scroll' );
	}

	/**
	 * Closes and destroys the modal
	 *
	 * @return {undefined}
	 */
	close() {
		// Fade out container
		fadeOut( this, () => {
			// Delete the node
			this.parentNode.removeChild( this );
		} );

		// Enable scroll on body
		document.body.classList.remove( 'uap-disable-scroll' );
	}

	/**
	 * Handles the action cancellation
	 *
	 * @return {undefined}
	 */
	cancel() {
		// Close the modal
		this.close();
	}

	/**
	 * Handles clicks outside the box and evaluates whether the it should close the dialog
	 * 
	 * @param {PointerEvent} event 
	 */
	_handleClickOutside( event ) {
		// Check if the dialog can only be closed when manually invoking .close()
		if ( this.forceManualClose ) {
			return;
		}

		// Check if the user is clicking any other element but the wrapper
		if ( event.target !== this.$wrapper ) {
			return;
		}

		// Otherwise (so if it's clicking the wrapper), close the dialog
		this.cancel();
	}

	/**
	 * Listens and handles clicks on the actions
	 *
	 * @param {PointerEvent} event The click event
	 * @return {undefined}
	 */
	_handleClick( event ) {
		// Check if the user is trying to cancel the action
		if ( event.target.closest( `[${ this.actionAttribute }="close"]` ) ) {
			// Cancel the action
			this.cancel();
		}
	}
}
